import { forwardRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export interface GWPTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
	size?: 'sm' | 'md' | 'lg';
	invalid?: boolean;
	resize?: 'vertical' | 'horizontal' | 'both' | 'none';
	onValueChange?: (value: string, event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onEnter?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const GWPTextarea = forwardRef<HTMLTextAreaElement, GWPTextareaProps>((props, ref) => {
	const {
		className,
		size = 'md',
		invalid = false,
		resize = 'vertical',
		onChange,
		onKeyDown,
		onValueChange,
		onEnter,
		disabled,
		readOnly,
		rows = 4,
		...rest
	} = props;

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange?.(event);
		onValueChange?.(event.target.value, event);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		onKeyDown?.(event);
		if (event.key === 'Enter') {
			onEnter?.(event);
		}
	};

	const classNames = combineClassNames(
		'gwp-textarea',
		size,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		readOnly ? 'read-only' : undefined,
		`resize-${resize}`,
		className,
	);

	return (
		<textarea
			{...rest}
			ref={ref}
			rows={rows}
			disabled={disabled}
			readOnly={readOnly}
			aria-invalid={invalid || rest['aria-invalid']}
			className={classNames}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
		/>
	);
});

GWPTextarea.displayName = 'GWPTextarea';

export default GWPTextarea;
